import { TableRow } from "@/components/ui";
import { getGenderAbbreviation, getInitials } from "@/lib/utils";

import {
  Avatar,
  AvatarFallback,
  BodyText,
  Button,
  TableCell,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useOpenPatientChart } from "@/hooks/useOpenPatientChart";
import { usePartner } from "@/hooks/usePartner";
import { trackEvent } from "@/lib/tracking";
import { selectFallbackPartnerUid } from "@/utils/partnerLogo";
import type { PatientSearchResult } from "./network/usePatientSearch";

interface PatientTableRowProps {
  patient: PatientSearchResult;
  includeVisitRfe: boolean;
  visitId?: string;
}

export const PatientTableRow = ({
  patient,
  includeVisitRfe,
  visitId,
}: PatientTableRowProps) => {
  const openPatientChart = useOpenPatientChart();

  const { partner } = usePartner(
    patient.preferredPartnerUid ??
      selectFallbackPartnerUid(patient.partnerUids),
  );

  const onClick = async (patient: PatientSearchResult) => {
    trackEvent(TRACKERS.OPEN_CHART_FROM_SEARCH);
    await openPatientChart({
      patientId: patient.patientId,
      medRecordDestination: "timeline",
    });
  };

  const onViewVisit = async (
    e: React.MouseEvent,
    patient: PatientSearchResult,
  ) => {
    e.stopPropagation();
    trackEvent(TRACKERS.OPEN_CHART_FROM_SEARCH);
    await openPatientChart({
      patientId: patient.patientId,
      encounterId: visitId,
      medRecordDestination: "timeline",
    });
  };

  return (
    <TableRow
      key={`${patient.patientId}`}
      className="w-12 hover:bg-secondary cursor-pointer"
      onClick={() => onClick(patient)}
      data-testid={`patient-table-row-${patient.patientId}`}
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="w-4 h-4">
            <AvatarFallback className="bg-yellow-400">
              <BodyText size="xs" className="text-[8px]">
                {getInitials(`${patient.firstName} ${patient.lastName}`)}
              </BodyText>
            </AvatarFallback>
          </Avatar>
          <BodyText size="sm" className="leading-tight">
            {patient.firstName} {patient.lastName}
          </BodyText>
        </div>
      </TableCell>
      <TableCell>
        <BodyText size="sm">{`${patient.age}${getGenderAbbreviation(patient.administrativeGender)}`}</BodyText>
      </TableCell>
      <TableCell>
        <BodyText size="sm">
          {patient.homeAddressState || (
            <BodyText size="sm" textColor="tertiary">
              n/a
            </BodyText>
          )}
        </BodyText>
      </TableCell>
      <TableCell>
        <BodyText size="sm">
          <img src={partner?.logoUrl} alt={partner?.name} className="w-4 h-4" />
        </BodyText>
      </TableCell>
      <TableCell>
        <BodyText size="sm">{patient.mrn || patient.userId}</BodyText>
      </TableCell>
      {includeVisitRfe && (
        <TableCell>
          <BodyText size="sm">{patient.visitRfe}</BodyText>
        </TableCell>
      )}
      <TableCell>
        <Button
          variant="primary"
          size="sm"
          className="leading-tight float-right"
          onClick={includeVisitRfe ? (e) => onViewVisit(e, patient) : undefined}
        >
          {includeVisitRfe ? "View Visit" : "Open Chart"}
        </Button>
      </TableCell>
    </TableRow>
  );
};
